import { useState, useEffect, useRef, forwardRef } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { useCountries } from "../../hooks/api/useCountries";
import { FlagIcon } from "../FlagIcon";
import styles from "./CountrySelector.module.css";

interface CountrySelectorProps {
  error?: string;
  placeholder?: string;
  onChange?: (event: { target: { value: string } }) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  name?: string;
  onCountryChange: (countryCode: string) => void;
}

export const CountrySelector = forwardRef<
  HTMLInputElement,
  CountrySelectorProps
>(({ error, placeholder, onChange, onBlur, name, onCountryChange }, ref) => {
  const { t } = useTranslation();
  const { countries, isLoading } = useCountries();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedCountry = countries.find(
    (country) => country.code === selectedValue,
  );

  const handleClickOutside = (event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleCountrySelect = (countryCode: string) => {
    setSelectedValue(countryCode);
    onCountryChange(countryCode);
    localStorage.setItem("countryCode", countryCode);

    setIsOpen(false);
  };

  const handleToggle = () => {
    if (!isLoading && countries.length > 0) {
      setIsOpen(!isOpen);
    }
  };

  const getDropdownButtonClasses = () => {
    const classes = [styles.dropdownButton];
    if (isOpen) classes.push(styles.isOpen);

    return classes.join(" ");
  };

  const getArrowIconClasses = () => {
    const classes = [styles.arrowIcon];
    if (isOpen) classes.push(styles.isOpen);
    return classes.join(" ");
  };

  const getDropdownItemClasses = (countryCode: string) => {
    const classes = [styles.dropdownItem];
    if (countryCode === selectedValue) classes.push(styles.isSelected);
    return classes.join(" ");
  };

  function getCountryCode() {
    const country = countries.find(
      (country) => country.code === localStorage.getItem("countryCode"),
    );

    if (country) {
      setSelectedValue(country.code);
      onCountryChange(country.code);
    }
  }

  useEffect(() => {
    getCountryCode();
  }, [countries]);

  const defaultPlaceholder = placeholder || t("selectCountry");

  return (
    <div className={styles.container} ref={containerRef}>
      <input
        ref={ref}
        type="hidden"
        name={name}
        value={selectedValue}
        onBlur={onBlur}
        onChange={onChange}
      />

      <label className={styles.label}>{t("country")}</label>

      <div className={styles.dropdownContainer}>
        <div
          className={getDropdownButtonClasses()}
          onClick={handleToggle}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleToggle();
            }
          }}
        >
          <div className={styles.selectedContent}>
            {selectedCountry ? (
              <>
                <div className={styles.flagIconWrapper}>
                  <FlagIcon
                    countryCode={selectedCountry.code}
                    size="medium"
                    alt={`Flag of ${selectedCountry.name}`}
                  />
                </div>
                <span className={styles.countryName}>
                  {selectedCountry.name}
                </span>
              </>
            ) : (
              <span className={styles.placeholder}>{defaultPlaceholder}</span>
            )}
          </div>

          <div className={getArrowIconClasses()}>
            <MdKeyboardArrowDown />
          </div>
          {isOpen && (
            <div className={styles.dropdownList}>
              {countries.map((country) => (
                <div
                  key={country.code}
                  className={getDropdownItemClasses(country.code)}
                  onClick={() => handleCountrySelect(country.code)}
                  role="option"
                  aria-selected={country.code === selectedValue}
                >
                  <div className={styles.flagIconWrapper}>
                    <FlagIcon
                      countryCode={country.code}
                      size="medium"
                      alt={`Flag of ${country.name}`}
                    />
                  </div>
                  <span className={styles.countryName}>{country.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {isOpen && (
          <div className={styles.dropdownList}>
            {countries.map((country) => (
              <div
                key={country.code}
                className={getDropdownItemClasses(country.code)}
                onClick={() => handleCountrySelect(country.code)}
                role="option"
                aria-selected={country.code === selectedValue}
              >
                <div className={styles.flagIconWrapper}>
                  <FlagIcon
                    countryCode={country.code}
                    size="medium"
                    alt={`Flag of ${country.name}`}
                  />
                </div>
                <span className={styles.countryName}>{country.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <label className={styles.errorMessage}>{error}</label>}
    </div>
  );
});
